import React, { useEffect, useState, useMemo } from "react";
import cs from "classnames";
import { NavLink } from "react-router-dom";
import { FormikProps } from "formik";

import { useRouter } from "../../../../lib/hooks/useRouter";
import { getWorkplaceIcon } from "../../../Workplaces/helpers/getWorkplaceInformation";
import { getWorkplaceChildren } from "../../../Workplaces/helpers/getWorkplaceChildren";
import { collectAllChildren } from "../../../../lib/helpers/collectWorkspaceTreeChildren";

import { Icon } from "../../../shared/Icon/Icon";
import { GroupFormFields } from "../lib/useGroupFormik";
import { WorkplaceFields } from "../../../../api/graphql/workplaces";

interface Props {
  item: WorkplaceFields;
  toggleParent: (value: boolean) => void;
  formik: FormikProps<GroupFormFields>;
  allToggled?: boolean;
}

export const GroupWorkspaceTreeLevel = (props: Props) => {
  const { item, toggleParent, formik, allToggled } = props;
  const { pathname } = useRouter();
  const isActive = pathname.includes(item.id);
  const [toggle, setToggle] = useState(() => isActive);

  useEffect(() => {
    if (allToggled !== undefined) {
      setToggle(allToggled);
    }
  }, [allToggled]);

  const icon = getWorkplaceIcon(item.__typename);
  const workplaceChildren = getWorkplaceChildren(item);
  const hasChildren = workplaceChildren.some((item) => item && item.length > 0);

  const allTargetTypeDescendants = useMemo(() => {
    const descendants = [item, ...collectAllChildren(item)].filter(
      (child) => child.__typename === "Room" || child.__typename === "Desk"
    );
    return descendants;
  }, [item]);

  const isSelected = useMemo(() => {
    return allTargetTypeDescendants.some((descendant) =>
      formik.values[descendant.__typename === "Desk" ? "desks" : "rooms"].some(
        (formikItem) => formikItem.id === descendant.id
      )
    );
  }, [allTargetTypeDescendants, formik.values]);

  const handleToggle = () => {
    setToggle((prev) => !prev);
  };

  const handleWorkspaceClick = () => {
    const deskDescendants: WorkplaceFields[] = [];
    const roomDescendants: WorkplaceFields[] = [];

    allTargetTypeDescendants.forEach((descendant) => {
      if (descendant.__typename === "Desk") {
        deskDescendants.push(descendant);
      } else if (descendant.__typename === "Room") {
        roomDescendants.push(descendant);
      }
    });

    if (deskDescendants.length > 0) {
      const currentDesks = formik.values.desks;
      const hasSelectedDesks = deskDescendants.some((desk) =>
        currentDesks.some((formikDesk) => formikDesk.id === desk.id)
      );

      if (!hasSelectedDesks) {
        const newDesks = deskDescendants.filter(
          (desk) =>
            !currentDesks.some((formikDesk) => formikDesk.id === desk.id)
        );
        formik.setFieldValue("desks", [...currentDesks, ...newDesks]);
      } else {
        const deskIdsToRemove = deskDescendants.map((desk) => desk.id);
        const updatedDesks = currentDesks.filter(
          (desk) => !deskIdsToRemove.includes(desk.id)
        );
        formik.setFieldValue("desks", updatedDesks);
      }
    }

    if (roomDescendants.length > 0) {
      const currentRooms = formik.values.rooms;
      const hasSelectedRooms = roomDescendants.some((room) =>
        currentRooms.some((formikRoom) => formikRoom.id === room.id)
      );

      if (!hasSelectedRooms) {
        const newRooms = roomDescendants.filter(
          (room) =>
            !currentRooms.some((formikRoom) => formikRoom.id === room.id)
        );
        formik.setFieldValue("rooms", [...currentRooms, ...newRooms]);
      } else {
        const roomIdsToRemove = roomDescendants.map((room) => room.id);
        const updatedRooms = currentRooms.filter(
          (room) => !roomIdsToRemove.includes(room.id)
        );
        formik.setFieldValue("rooms", updatedRooms);
      }
    }
  };

  const classes = cs("WorkplacesTreeLevel", {
    "WorkplacesTreeLevel--open": toggle,
    "WorkplacesTreeLevel--active": isActive,
    "WorkplacesTreeLevel--space": !hasChildren,
  });

  const subLevelClasses = cs("WorkplacesTreeLevel__sub-level", {
    "WorkplacesTreeLevel__sub-level--open": toggle,
  });

  return (
    <>
      <div className={classes}>
        <div className="WorkplacesTreeLevel__wrapper">
          {hasChildren && (
            <span
              onClick={handleToggle}
              className="WorkplacesTreeLevel__toggle"
            ></span>
          )}
          <NavLink
            className="WorkplacesTreeLevel__link"
            onClick={handleWorkspaceClick}
            to={"#"}
          >
            <div className="WorkplacesTreeLevel__icon">
              <Icon icon={icon} />
            </div>
            <h3 className="WorkplacesTreeLevel__name">{item.name}</h3>
          </NavLink>
          <div
            className="WorkplacesTreeLevel__status"
            onClick={handleWorkspaceClick}
          >
            <Icon icon={isSelected ? "checkbox-full" : "checkbox-empty"} />
          </div>
        </div>
      </div>
      {hasChildren && (
        <div className={subLevelClasses}>
          {workplaceChildren.map((child) =>
            child
              ?.filter((workplace) => workplace.__typename !== "Wayfinder")
              .map((workplace) => (
                <GroupWorkspaceTreeLevel
                  item={workplace}
                  key={`${workplace.id}-group-tree-level`}
                  formik={formik}
                  toggleParent={(value) => {
                    setToggle(value);
                    toggleParent(value);
                  }}
                  allToggled={allToggled}
                />
              ))
          )}
        </div>
      )}
    </>
  );
};
